/**
 * Image Capture Utilities for Top Priority App
 * 
 * Provides functions for capturing page sections as PNG images
 * using dom-to-image library.
 */

import { getTimestamp } from './excelUtils.js';

const DOM_TO_IMAGE_CDN = 'https://cdn.jsdelivr.net/npm/dom-to-image@2.6.0/dist/dom-to-image.min.js';

/**
 * Load dom-to-image library on demand
 */
async function loadDomToImage() {
	if (window.domtoimage) return window.domtoimage;
	
	await new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = DOM_TO_IMAGE_CDN;
		script.onload = resolve;
		script.onerror = reject;
		document.head.appendChild(script);
	});
	
	return window.domtoimage;
}

/**
 * Convert external images to inline base64 URIs so dom-to-image captures them
 * Returns a restore function to revert the changes
 */
async function inlineSvgImages(root) {
	const svgImages = Array.from(root.querySelectorAll('image'));
	const htmlImages = Array.from(root.querySelectorAll('img[src$=".svg"]'));
	
	const originals = [
		...svgImages.map(img => ({
			el: img,
			src: img.getAttribute('href'),
			attr: 'href'
		})),
		...htmlImages.map(img => ({
			el: img,
			src: img.getAttribute('src'),
			attr: 'src'
		}))
	].filter(item => item.src && !item.src.startsWith('data:'));

	await Promise.all(
		originals.map(async ({ el, src, attr }) => {
			try {
				const fullSrc = src.startsWith('/') 
					? window.location.origin + src 
					: new URL(src, window.location.href).href;
				const res = await fetch(fullSrc);
				const blob = await res.blob();
				const reader = new FileReader();
				const dataURL = await new Promise((resolve) => {
					reader.onloadend = () => resolve(reader.result);
					reader.readAsDataURL(blob);
				});
				el.setAttribute(attr, dataURL);
			} catch (e) {
				console.warn('Failed to inline image', src, e);
			}
		})
	);

	return () => {
		originals.forEach(({ el, src, attr }) => el.setAttribute(attr, src));
	};
}

/**
 * Trigger download of a blob as a file
 */
function downloadBlob(blob, filename) {
	const link = document.createElement('a');
	link.download = filename;
	link.href = URL.createObjectURL(blob);
	link.click();
	
	// Clean up blob URL
	setTimeout(() => URL.revokeObjectURL(link.href), 100);
}

/**
 * Capture a specific element as an image
 * 
 * @param {HTMLElement} element - Element to capture
 * @param {Object} options - Capture options
 * @param {string} options.filename - Filename for download (without extension)
 * @param {Object} options.style - Additional styles for capture
 */
export async function captureElementAsImage(element, options = {}) {
	const {
		filename = `capture-${getTimestamp()}`,
		style = {}
	} = options;

	const domtoimage = await loadDomToImage();
	
	// Inline SVG images before capture
	const restoreImages = await inlineSvgImages(element);

	try {
		// Create high-quality PNG blob
		const blob = await domtoimage.toBlob(element, {
			quality: 1.0,
			bgcolor: '#ffffff',
			style: {
				padding: '20px',
				background: '#ffffff',
				...style
			}
		});

		// Restore original image sources
		restoreImages && restoreImages();

		// Download
		downloadBlob(blob, `${filename}.png`);
		
	} catch (error) {
		// Restore images even on error
		restoreImages && restoreImages();
		throw error;
	}
}

/**
 * Capture the results section as an image
 * 
 * @param {string} [filename] - Optional custom filename
 * @throws {Error} If results section not found
 */
export async function captureResultsAsImage(filename) {
	const resultsSection = document.getElementById('results-section');
	if (!resultsSection) {
		throw new Error('Results section not found');
	}

	await captureElementAsImage(resultsSection, {
		filename: filename || `TopPriority-results-${getTimestamp()}`
	});
}

/**
 * Capture the full page as an image
 * Handles scrolling, PayPal element removal, and restoration
 * 
 * @param {string} [filename] - Optional custom filename
 */
export async function captureFullPageAsImage(filename) {
	const domtoimage = await loadDomToImage();
	
	// Scroll to top for consistent capture
	const previousScrollY = window.scrollY;
	window.scrollTo(0, 0);
	await new Promise((r) => requestAnimationFrame(r));

	const target = document.body;

	// Inline SVG images before capture
	const restoreImages = await inlineSvgImages(target);

	// Temporarily remove PayPal elements from DOM to avoid CORS errors
	const paypalElements = [
		...target.querySelectorAll('form[action*="paypal.com"]'),
		...target.querySelectorAll('img[src*="paypal"]'),
		...target.querySelectorAll('img[src*="paypalobjects.com"]')
	];
	const removedElements = paypalElements.map(el => ({
		element: el,
		parent: el.parentNode,
		nextSibling: el.nextSibling
	}));
	
	paypalElements.forEach(el => el.remove());

	try {
		// Create high-quality PNG blob
		const blob = await domtoimage.toBlob(target, {
			quality: 1.0,
			bgcolor: '#ffffff',
			width: target.scrollWidth,
			height: target.scrollHeight,
			style: {
				transform: 'scale(1)',
				transformOrigin: 'top left'
			}
		});

		// Download
		downloadBlob(blob, `${filename || `TopPriority-analysis-${getTimestamp()}`}.png`);

	} finally {
		// Restore scroll position
		window.scrollTo(0, previousScrollY);

		// Restore original image sources
		restoreImages && restoreImages();
		
		// Re-insert PayPal elements
		removedElements.forEach(({ element, parent, nextSibling }) => {
			if (parent) {
				if (nextSibling) {
					parent.insertBefore(element, nextSibling);
				} else {
					parent.appendChild(element);
				}
			}
		});
	}
}
